import { useLangue } from '../../composants/useLangue.js';
import { texteEnLangue } from '@artosera/shared';
import { Badge, Dialog, Input } from '@artosera/ui';
import type { BadgeTone } from '@artosera/ui';
import { CalendarRange, Contact, Image as ImageIcon, Plus, Users } from 'lucide-react';
import type { JSX, KeyboardEvent } from 'react';
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useOeuvres } from '../../api/oeuvres.js';
import { useArtistes } from '../../api/artistes.js';
import { useContacts } from '../../api/contacts.js';
import { useExpositions } from '../../api/expositions.js';
import type { GalerieSession } from '../../api/types.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { couleurDuStatut } from '../oeuvres/statutUi.js';
import { teinteDeLEtat } from '../expositions/etatUi.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import { PanneauDemander } from '../assistant-recherche/PanneauDemander.js';
import { entreeEnLectureSeule, type EntreeNavigation } from './navigation.js';
import { correspondA, saisieBornee } from './rechercheTexte.js';
import styles from './CommandPalette.module.css';

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entrees: readonly EntreeNavigation[];
  galerie: GalerieSession;
  galerieSlug: string;
  /** L'assistant de recherche est ouvert à cette personne : l'onglet « Demander » apparaît. */
  demander?: boolean;
}

type Onglet = 'rechercher' | 'demander';

interface ActionPalette {
  readonly cle: string;
  readonly libelle: string;
  readonly chemin: string;
}

interface Option {
  readonly id: string;
  readonly libelle: string;
  readonly aller: () => void;
  readonly icone?: JSX.Element;
  readonly pastille?: { readonly texte: string; readonly tone: BadgeTone };
  readonly detail?: string;
}

interface Groupe {
  readonly cle: string;
  readonly titre: string;
  readonly options: readonly Option[];
  readonly voirTout?: { readonly chemin: string; readonly libelle: string };
}

/** Longueur minimale avant d'interroger l'API (mission ARTO-17, point 2). */
const LONGUEUR_MIN_RECHERCHE = 2;
const LIMITE_PAR_GROUPE = 5;

/**
 * Recherche globale ⌘K (3.2, ARTO-17) : navigation vers les modules et les
 * deux actions de création (recherche locale, synchrone), et — à partir de
 * deux caractères, débattus — œuvres, artistes, contacts et expositions
 * (recherche `q`, sur l'API réelle ou les fixtures en mode démo). Résultats
 * groupés par type, cinq par groupe, « Voir tout » vers la liste. Rôle
 * `combobox`/`listbox` (D15 accessibilité) : les flèches déplacent
 * `aria-activedescendant`, Entrée ouvre la fiche active, Échap ferme (Radix).
 */
export function CommandPalette({
  open,
  onOpenChange,
  entrees,
  galerie,
  galerieSlug,
  demander = false,
}: CommandPaletteProps): JSX.Element {
  const langue = useLangue();
  const { t } = useTranslation(['commun', 'regie', 'oeuvres']);
  const [recherche, setRecherche] = useState('');
  const [indexActif, setIndexActif] = useState(0);
  const [ongletChoisi, setOnglet] = useState<Onglet>('rechercher');
  const onglet: Onglet = demander ? ongletChoisi : 'rechercher';
  const navigate = useNavigate();
  const idListe = useId();
  const idOnglets = useId();

  // Bornée : au-delà de 200 caractères, les quatre listes répondent 400 d'un coup.
  const rechercheDebattue = useDebounce(saisieBornee(recherche), 300);
  const rechercheDonnees = open && rechercheDebattue.length >= LONGUEUR_MIN_RECHERCHE;

  const voitOeuvres =
    rechercheDonnees &&
    galerieAModule(galerie, 'oeuvres') &&
    galerieALaction(galerie, 'artworks:read');
  const voitArtistes =
    rechercheDonnees &&
    galerieAModule(galerie, 'artistes') &&
    galerieALaction(galerie, 'artists:read');
  const voitContacts =
    rechercheDonnees &&
    galerieAModule(galerie, 'contacts') &&
    galerieALaction(galerie, 'contacts:read');
  const voitExpositions =
    rechercheDonnees &&
    galerieAModule(galerie, 'expositions') &&
    galerieALaction(galerie, 'exhibitions:read');

  const oeuvres = useOeuvres(
    galerieSlug,
    { q: rechercheDebattue, tri: 'pertinence', parPage: LIMITE_PAR_GROUPE },
    { enabled: voitOeuvres },
  );
  const artistes = useArtistes(
    galerieSlug,
    { q: rechercheDebattue, parPage: LIMITE_PAR_GROUPE },
    { enabled: voitArtistes },
  );
  const contacts = useContacts(
    galerieSlug,
    { q: rechercheDebattue, parPage: LIMITE_PAR_GROUPE },
    { enabled: voitContacts },
  );
  const expositions = useExpositions(
    galerieSlug,
    { q: rechercheDebattue, parPage: LIMITE_PAR_GROUPE },
    { enabled: voitExpositions },
  );

  // Droits visibles (ARTO-53, § 4) : le comptable n'a pas `artworks:write`,
  // donc pas de « Nouvelle œuvre » dans la palette non plus (audit du
  // 23 septembre 2026, § 1) — même helper `galerieALaction` que partout.
  const actions: readonly ActionPalette[] = useMemo(
    () => [
      ...(galerieALaction(galerie, 'artworks:write')
        ? [
            {
              cle: 'nouvelle-oeuvre',
              libelle: t('regie:coquille.actionNouvelleOeuvre'),
              chemin: `/${galerieSlug}/oeuvres?nouvelle=1`,
            },
          ]
        : []),
      ...(galerieALaction(galerie, 'contacts:write')
        ? [
            {
              cle: 'nouveau-contact',
              libelle: t('regie:coquille.actionNouveauContact'),
              chemin: `/${galerieSlug}/contacts?nouveau=1`,
            },
          ]
        : []),
    ],
    [galerie, galerieSlug, t],
  );

  function aller(chemin: string): void {
    navigate(chemin);
    onOpenChange(false);
    setRecherche('');
    setIndexActif(0);
  }

  // Sans accent ni casse, « œ » comme « oe » : « oeuvre » trouve « Œuvres ».
  const modulesFiltres = entrees.filter((entree) =>
    correspondA(t(entree.libelleNavigation), recherche),
  );
  const actionsFiltrees = actions.filter((action) => correspondA(action.libelle, recherche));

  const groupes: Groupe[] = [
    {
      cle: 'modules',
      titre: t('regie:coquille.rechercheTitreModules'),
      options: modulesFiltres.map((entree) => {
        const Icone = entree.icone;
        return {
          id: `module-${entree.cle}`,
          libelle: t(entree.libelleNavigation),
          icone: <Icone size={16} strokeWidth={1.5} aria-hidden />,
          // Module en lecture seule : il s'ouvre, la pastille dit pourquoi on
          // n'y crée rien. Les commandes de création ne concernent que le
          // cœur (œuvre, contact) : aucune n'est à verrouiller ici.
          ...(entreeEnLectureSeule(galerie, entree)
            ? {
                pastille: {
                  texte: t('regie:module.lectureSeule.etiquette'),
                  tone: 'neutral' as BadgeTone,
                },
              }
            : {}),
          aller: () => aller(`/${galerieSlug}/${entree.chemin}`),
        };
      }),
    },
    {
      cle: 'actions',
      titre: t('regie:coquille.rechercheTitreActions'),
      options: actionsFiltrees.map((action) => ({
        id: `action-${action.cle}`,
        libelle: action.libelle,
        icone: <Plus size={16} strokeWidth={1.5} aria-hidden />,
        aller: () => aller(action.chemin),
      })),
    },
    {
      cle: 'oeuvres',
      titre: t('regie:coquille.rechercheTitreOeuvres'),
      options: voitOeuvres
        ? (oeuvres.data?.oeuvres ?? []).map((oeuvre) => ({
            id: `oeuvre-${oeuvre.id}`,
            libelle: oeuvre.titre,
            detail: oeuvre.artisteNom,
            icone: <ImageIcon size={16} strokeWidth={1.5} aria-hidden />,
            pastille: {
              texte: t(`oeuvres:statuts.${oeuvre.statut}`),
              tone: couleurDuStatut(oeuvre.statut),
            },
            aller: () => aller(`/${galerieSlug}/oeuvres/${oeuvre.id}`),
          }))
        : [],
      voirTout: {
        chemin: `/${galerieSlug}/oeuvres?q=${encodeURIComponent(rechercheDebattue)}`,
        libelle: t('regie:coquille.rechercheVoirTout'),
      },
    },
    {
      cle: 'artistes',
      titre: t('regie:coquille.rechercheTitreArtistes'),
      options: voitArtistes
        ? (artistes.data?.artistes ?? []).map((artiste) => ({
            id: `artiste-${artiste.id}`,
            libelle: artiste.nom,
            icone: <Users size={16} strokeWidth={1.5} aria-hidden />,
            pastille: {
              texte: t(
                artiste.representation.representee
                  ? 'regie:artistes.representee'
                  : 'regie:artistes.nonRepresentee',
              ),
              tone: (artiste.representation.representee ? 'accent' : 'neutral') as BadgeTone,
            },
            aller: () => aller(`/${galerieSlug}/artistes/${artiste.id}`),
          }))
        : [],
      voirTout: {
        chemin: `/${galerieSlug}/artistes?q=${encodeURIComponent(rechercheDebattue)}`,
        libelle: t('regie:coquille.rechercheVoirTout'),
      },
    },
    {
      cle: 'contacts',
      titre: t('regie:coquille.rechercheTitreContacts'),
      options: voitContacts
        ? (contacts.data?.contacts ?? []).map((contact) => ({
            id: `contact-${contact.id}`,
            libelle: contact.nomAffiche,
            icone: <Contact size={16} strokeWidth={1.5} aria-hidden />,
            pastille: {
              texte: t(
                contact.genre === 'organisation'
                  ? 'regie:contacts.nouveauContact.genreOrganisation'
                  : 'regie:contacts.nouveauContact.genrePersonne',
              ),
              tone: 'neutral' as BadgeTone,
            },
            aller: () => aller(`/${galerieSlug}/contacts/${contact.id}`),
          }))
        : [],
      voirTout: {
        chemin: `/${galerieSlug}/contacts?q=${encodeURIComponent(rechercheDebattue)}`,
        libelle: t('regie:coquille.rechercheVoirTout'),
      },
    },
    {
      cle: 'expositions',
      titre: t('regie:coquille.rechercheTitreExpositions'),
      options: voitExpositions
        ? (expositions.data?.expositions ?? []).map((exposition) => ({
            id: `exposition-${exposition.id}`,
            libelle: texteEnLangue(exposition.titre, langue),
            icone: <CalendarRange size={16} strokeWidth={1.5} aria-hidden />,
            pastille: {
              texte: t(`regie:expositions.etats.${exposition.etat}`),
              tone: teinteDeLEtat(exposition.etat),
            },
            aller: () => aller(`/${galerieSlug}/expositions/${exposition.id}`),
          }))
        : [],
      voirTout: {
        chemin: `/${galerieSlug}/expositions?q=${encodeURIComponent(rechercheDebattue)}`,
        libelle: t('regie:coquille.rechercheVoirTout'),
      },
    },
  ];

  const groupesAffiches = groupes.filter((groupe) => groupe.options.length > 0);
  const optionsPlates = groupesAffiches.flatMap((groupe) => groupe.options);
  const chargementDonnees =
    rechercheDonnees &&
    ((voitOeuvres && oeuvres.isLoading) ||
      (voitArtistes && artistes.isLoading) ||
      (voitContacts && contacts.isLoading) ||
      (voitExpositions && expositions.isLoading));
  const aucunResultat = optionsPlates.length === 0 && !chargementDonnees;
  const indexBorne =
    optionsPlates.length === 0 ? -1 : Math.min(indexActif, optionsPlates.length - 1);
  const optionActive = indexBorne >= 0 ? optionsPlates[indexBorne] : undefined;

  function surClavier(event: KeyboardEvent<HTMLInputElement>): void {
    if (optionsPlates.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIndexActif((valeur) => Math.min(optionsPlates.length - 1, valeur + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setIndexActif((valeur) => Math.max(0, valeur - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      optionActive?.aller();
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(prochain) => {
        onOpenChange(prochain);
        if (!prochain) {
          setRecherche('');
          setIndexActif(0);
          // Fermer efface l'échange de « Demander » : le panneau se démonte.
          setOnglet('rechercher');
        }
      }}
      title={t(
        onglet === 'demander' ? 'regie:assistantRecherche.titre' : 'regie:coquille.rechercheOuvrir',
      )}
      closeLabel={t('commun:actions.fermer')}
    >
      {demander ? (
        <div
          className={styles.onglets}
          role="tablist"
          aria-label={t('regie:coquille.rechercheOuvrir')}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
            event.preventDefault();
            setOnglet(onglet === 'rechercher' ? 'demander' : 'rechercher');
          }}
        >
          {(['rechercher', 'demander'] as const).map((cle) => (
            <button
              key={cle}
              type="button"
              role="tab"
              id={`${idOnglets}-${cle}`}
              aria-selected={onglet === cle}
              aria-controls={`${idOnglets}-panneau`}
              tabIndex={onglet === cle ? 0 : -1}
              className={`${styles.onglet} ${onglet === cle ? styles.ongletActif : ''}`}
              onClick={() => setOnglet(cle)}
            >
              {t(
                cle === 'rechercher'
                  ? 'regie:coquille.rechercheOnglet'
                  : 'regie:assistantRecherche.onglet',
              )}
            </button>
          ))}
        </div>
      ) : null}
      {onglet === 'demander' ? (
        <div role="tabpanel" id={`${idOnglets}-panneau`} aria-labelledby={`${idOnglets}-demander`}>
          <PanneauDemander
            galerie={galerie}
            galerieSlug={galerieSlug}
            surNavigation={() => onOpenChange(false)}
          />
        </div>
      ) : (
        <div role={demander ? 'tabpanel' : undefined} id={`${idOnglets}-panneau`}>
          <Input
            autoFocus
            type="search"
            role="combobox"
            aria-expanded={optionsPlates.length > 0}
            aria-controls={idListe}
            aria-autocomplete="list"
            aria-activedescendant={optionActive?.id}
            className={styles.champ}
            placeholder={t('regie:coquille.recherchePlaceholder')}
            aria-label={t('regie:coquille.recherchePlaceholder')}
            value={recherche}
            onChange={(event) => {
              setRecherche(event.target.value);
              setIndexActif(0);
            }}
            onKeyDown={surClavier}
          />
          <div
            className={styles.resultats}
            role="listbox"
            id={idListe}
            aria-label={t('regie:coquille.rechercheOuvrir')}
          >
            {aucunResultat ? (
              <p className={styles.vide}>{t('regie:coquille.rechercheAucunResultat')}</p>
            ) : (
              groupesAffiches.map((groupe) => (
                <section key={groupe.cle} className={styles.groupe}>
                  <h3 className={styles.section}>{groupe.titre}</h3>
                  <ul className={styles.liste}>
                    {groupe.options.map((option) => (
                      <li key={option.id}>
                        <button
                          type="button"
                          id={option.id}
                          role="option"
                          aria-selected={option.id === optionActive?.id}
                          className={`${styles.resultat} ${option.id === optionActive?.id ? styles.resultatActif : ''}`}
                          onMouseEnter={() =>
                            setIndexActif(optionsPlates.findIndex((o) => o.id === option.id))
                          }
                          onClick={option.aller}
                        >
                          {option.icone ? (
                            <span className={styles.resultatIcone} aria-hidden="true">
                              {option.icone}
                            </span>
                          ) : null}
                          <span className={styles.resultatTextes}>
                            <span className={styles.resultatLibelle}>{option.libelle}</span>
                            {option.detail ? (
                              <span className={styles.resultatDetail}>{option.detail}</span>
                            ) : null}
                          </span>
                          {option.pastille ? (
                            <Badge tone={option.pastille.tone}>{option.pastille.texte}</Badge>
                          ) : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                  {groupe.voirTout ? (
                    <a
                      className={styles.voirTout}
                      href={groupe.voirTout.chemin}
                      onClick={(event) => {
                        event.preventDefault();
                        aller(groupe.voirTout?.chemin ?? '');
                      }}
                    >
                      {groupe.voirTout.libelle}
                    </a>
                  ) : null}
                </section>
              ))
            )}
            {chargementDonnees ? (
              <p className={styles.vide}>{t('regie:commun.chargement')}</p>
            ) : null}
          </div>
        </div>
      )}
    </Dialog>
  );
}
